import React from 'react';
import {
  get,
  noop
} from 'lodash';
import {
  ELSLoggingService
} from '@els/els-ui-common-react';
import axios, { AxiosResponse } from 'axios';
import {
  fetchCrosswalkUser,
  fetchUser,
  fetchUsersByIds
} from '../../apis/eols-user-management-service/eols-user-management-service.utilities';
import {
  CrosswalkUserDto,
  SystemType,
  UsersByIdDto
} from '../../apis/eols-user-management-service/eols-user-management-service.dtos';
import { AppContextState } from './AppProvider.component';
import {
  removeCookies,
  setAppLinkCookies
} from '../../utilities/app-link-redirect.utilities';
import {
  fetchAppLinkData,
  fetchHashLink,
  getExternalAppRedirectUrl,
  getLinkNavigation
} from '../../apis/eols-app-link/eols-app-link.utilities';
import { scrubProps } from '../../utilities/analytics.utilities';
import {
  getDefaultActionProps,
  shouldGetNewOsmosisToken
} from './app-state-selectors.utilities';
import {
  fetchGroupedFeatureFlags,
  fetchGroupFeatureFlag,
  postGroupFeatureFlag
} from '../../apis/eols-features-api/eols-features-api.utilities';
import {
  getActiveABTestFlags,
  getRandomABTestFlavor
} from '../../utilities/featureFlag.utilities';
import { FeatureFlagsGroupedDto } from '../../apis/eols-features-api/eols-features-api.dtos';
import { fetchCourseSection as fetchCourseSectionService } from '../../apis/eols-course-crud/eols-course-crud.utilities';
import { CourseSectionDto } from '../../apis/eols-course-crud/eols-course-crud.dtos';
import { AppState } from './app-state.types';
import {
  fetchQuizSessionByRecommendationAttemptId,
  fetchRemediationAssignmentWithSources,
  fetchRecommendationAttempts,
  fetchRaasAssessment,
  fetchRecommendationItems,
  fetchRecommendations,
  postRaaSQuizSession,
  postRecommendationAttempt,
  putRecommendation,
  putRecommendationAttempt,
  fetchEbookReadingSsoUrl,
  fetchEbookSectionSsoUrl,
  submitReviewAttempt,
  fetchAssessmentsByRemediationAssignmentId,
  fetchMyRaasAssessments,
  fetchHesiExamInfoByExamEditionId,
  patchRaasAssessmentLastAccessed,
  fetchRaasAssessmentSource,
  fetchAssessmentMetrics,
  fetchSherpathAIContext,
  fetchRecommendationSources,
  fetchRecommendationFeedback,
  postRecommendationFeedback,
  patchRecommendationFeedback
} from '../../apis/nhe-remediation-service/nhe-remediation-service.utilities';
import {
  fetchEolsAssessmentById,
  fetchEolsAssessmentSubmissions,
  postAssessment,
  postSubmission
} from '../../apis/eols-assessment-service/eols-assessment-service.utilities';
import {
  AppLinkOutConfig,
  AppLinkOutResponse
} from '../../apis/eols-app-link/eols-app-link.models';
import { Application } from '../../apis/eols-app-link/eols-app-link.constants';
import {
  EbookSectionSsoRequestDto,
  EbookReadingRequestDto,
  EbookReadingResponseDto,
  HesiExamInfoDto,
  NewQuizSessionDto,
  QuizSessionDto,
  RaasAssessmentDto,
  RaasAssessmentMetricsDto,
  RaasAssessmentSourceDto,
  RaasAssessmentWithAssignmentDto,
  RecommendationAttemptDto,
  RecommendationDto,
  RecommendationFeedbackDto,
  RecommendationItemDto,
  SherpathAIContextDto,
  RecommendationSourceDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { fetchCaseStudy } from '../../apis/ocs-api-service/ocs-api-service.utilities';
import {
  AssessmentSubmissionDto,
  EolsAssessmentDto
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { fetchOsmosisToken } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.utilities';
import { OsmosisTokenDto } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import {
  fetchOsmosisLearnData,
  fetchOsmosisTranscript
} from '../../apis/osmosis-service/osmosis-service.utilities';
import { OsmosisLearnData } from '../../apis/osmosis-service/osmosis-service.dtos';
import {
  NewRelicAnalyticsAction,
  NewRelicAnalyticsActionProps
} from '../../models/new-relic-analytics.models';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsActionProps,
  AdobeAnalyticsPageData
} from '../../models/adobe-analytics.models';
import { fetchIpAddress } from '../../apis/iipify/ipify.service';
import type { RemediationBaseState } from '../../hocs/with-remediation-base/withRemediationBase.hoc';
import { getDefaultAdobeAnalyticsPageData } from '../analytics-registrar/analytics-registrar.utilities';

const fileName = 'app.state.actions.ts';

export const trackNewRelicAction = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  actionProps: NewRelicAnalyticsActionProps;
}): void => {
  const appState = props.appStateRef.current;
  const defaultActionProps = getDefaultActionProps(appState);
  const actionProps = scrubProps(props.actionProps.props);
  const _props = {
    ...defaultActionProps,
    ...actionProps
  };
  ELSLoggingService.info(fileName, `New Relic PageAction: ${props.actionProps.action}, ${JSON.stringify(_props)}`);
  if (!window.newrelic) {
    return;
  }
  window.newrelic.addPageAction(props.actionProps.action, _props);
};

export const trackAdobeAction = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  actionProps: AdobeAnalyticsActionProps;
}): void => {
  const appState = props.appStateRef.current;
  const defaultPageData = getDefaultAdobeAnalyticsPageData(appState);
  const pageData = {
    ...defaultPageData,
    ...props.actionProps.pageData
  };
  try {
    window.pageDataTracker.trackEvent?.(
      props.actionProps.action,
      pageData
    );
  } catch (e) {
    noop(e);
  }
};

export const captureNewPageDataAction = (props: AppContextState & {
  pageData: Partial<AdobeAnalyticsPageData>;
}): void => {
  const { setAppState, pageData } = props;
  setAppState((prevState) => ({
    ...prevState,
    pendingNewPageAnalytics: {
      pageData,
      startTimestamp: Date.now()
    }
  }));
};

export const trackNewPageLoadAction = (props: AppContextState): void => {
  const { appStateRef, setAppState } = props;
  const pending = appStateRef.current.pendingNewPageAnalytics;
  if (!pending) {
    return;
  }
  const loadTime = Date.now() - pending.startTimestamp;
  trackAdobeAction({
    appStateRef,
    actionProps: {
      action: AdobeAnalyticsAction.NEW_PAGE,
      pageData: {
        ...pending.pageData,
        page: {
          ...pending.pageData.page,
          loadTime
        }
      }
    }
  });
  setAppState((prevState) => ({
    ...prevState,
    pendingNewPageAnalytics: null
  }));
};

const requestStart = (props: { setAppState: AppContextState['setAppState'] }) => {
  props.setAppState((prevState) => {
    return {
      ...prevState,
      pendingRequestCount: prevState.pendingRequestCount + 1
    };
  });
};

const requestEnd = (props: { setAppState: AppContextState['setAppState'] }) => {
  props.setAppState((prevState) => {
    return {
      ...prevState,
      pendingRequestCount: prevState.pendingRequestCount - 1
    };
  });
};

const handleRequestError = (props: AppContextState & {
  error: Error;
  action: string;
  isTrackRequestCount: boolean;
}) => {

  const {
    error,
    action,
    isTrackRequestCount,
  } = props;

  const url = get(error, 'config.url', '');
  const method = get(error, 'config.method', '');
  const status = get(error, 'response.status', '');
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (status === 400) {
    // Likely 400 error here due to overloaded headers
    removeCookies();
  }

  trackNewRelicAction({
    appStateRef: props.appStateRef,
    actionProps: {
      action: NewRelicAnalyticsAction.API_ERROR,
      props: {
        status,
        action,
        url,
        method
      }
    }
  });

  if (isTrackRequestCount) {
    requestEnd(props);
  }

  return Promise.reject(error);
};
export const fetchEvolveUsersAction = (props: AppContextState & {
  useCache: boolean;
  userId: string;
}): Promise<unknown> => {
  const {
    useCache,
    appStateRef,
    setAppState,
    userId
  } = props;

  if (useCache && appStateRef.current.evolveUser && appStateRef.current.evolveUser.eolsId.toString() === userId) {
    return Promise.resolve(appStateRef.current.evolveUser);
  }

  requestStart(props);
  return fetchCrosswalkUser(userId, SystemType.EVOLVETYPE)
    .then((crosswalkUser: CrosswalkUserDto) => {
      setAppState((prevState) => {
        return {
          ...prevState,
          evolveUser: crosswalkUser
        };
      });
      requestEnd(props);
      return crosswalkUser;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchCrosswalkUser',
      isTrackRequestCount: true
    }));
};

export const fetchEolsUserAction = (props: AppContextState & {
  userId: string;
  useCache: boolean;
}): Promise<unknown> => {

  const {
    setAppState,
    useCache,
    appStateRef,
    userId
  } = props;

  if (!props.userId) {
    return Promise.resolve(null);
  }

  if (useCache && appStateRef.current.eolsUser && appStateRef.current.eolsUser.id.toString() === userId) {
    return Promise.resolve(appStateRef.current.eolsUser);
  }

  requestStart(props);
  return fetchUser(userId)
    .then((user) => {
      setAppState((prevState) => {
        return {
          ...prevState,
          eolsUser: user
        };
      });
      requestEnd(props);
      return user;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchEolsUserAction',
      isTrackRequestCount: true
    }));
};

export const fetchUserAction = (props: AppContextState & {
  userId: string;
}): Promise<unknown> => {
  const {
    userId,
    setAppState,
  } = props;

  if (!userId) {
    return Promise.resolve(null);
  }
  requestStart({ setAppState });
  return fetchUser(userId)
    .then((user) => {
      requestEnd({ setAppState });
      return user;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchUserAction',
      isTrackRequestCount: true
    }));
};

export const fetchAppLinkDataAction = (props: AppContextState & {
  appLinkId: string;
}): Promise<unknown> => {
  const {
    appLinkId,
    setAppState,
  } = props;

  requestStart(props);
  return fetchAppLinkData(appLinkId)
    .then((appLinkData) => {
      // Cast to AppState expected generic union; runtime shape is validated elsewhere.
      setAppState((prevState) => {
        return {
          ...prevState,
          appLinkData: appLinkData as typeof prevState.appLinkData,
          pendingRequestCount: prevState.pendingRequestCount - 1
        };
      });
      return appLinkData as typeof props.appState.appLinkData;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchAppLinkDataAction',
      isTrackRequestCount: true
    }));
};

export const fetchHashLinkAction = (props: AppContextState & {
  linkHash: string;
}): Promise<unknown> => {
  const {
    linkHash,
    appState,
    appStateRef,
    setAppState,
  } = props;
  requestStart({ setAppState });
  return fetchHashLink(linkHash)
    .then((hashLinkDto) => {
      setAppState((prevState) => {
        return {
          ...prevState,
          appLinkCookies: hashLinkDto,
          pendingRequestCount: prevState.pendingRequestCount - 1
        };
      });
      return hashLinkDto;
    })
    .catch((e) => handleRequestError({
      appState,
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchHashLinkAction',
      isTrackRequestCount: true
    }));

};

const postGroupFeatureFlagAction = (props: {
  eolsApp: string;
  featureName: string;
  group: string;
  featureValue: string;
}) => {
  const {
    eolsApp,
    featureName,
    group,
    featureValue
  } = props;
  return postGroupFeatureFlag(eolsApp, featureName, group, featureValue).then(() => {
    return {
      createdAt: null,
      eolsApp,
      featureName,
      featureValue,
      group,
      updatedAt: null
    };
  });
};

const fetchABFlavorAction = (props: AppContextState & {
  abTestFlag: FeatureFlagsGroupedDto;
}) => {
  const {
    appStateRef,
    setAppState,
    abTestFlag
  } = props;
  const abTestFlavor = appStateRef.current.abTestFlavors.find((flag) => {
    return flag.featureName === abTestFlag.featureName;
  });
  if (abTestFlavor) {
    return Promise.resolve(abTestFlavor);
  }
  requestStart({ setAppState });
  const app = `${Application.REMEDIATION}_${abTestFlag.featureName}`;
  const key = abTestFlag.featureName;
  return fetchGroupFeatureFlag(app, key, appStateRef.current.courseSectionId)
    .catch((error) => {
      if (error.response && error.response.status === 404) {
        const newFlavor = getRandomABTestFlavor(abTestFlag);
        if (!newFlavor) {
          ELSLoggingService.error(fileName, `AB test config missing for flag: ${key}`);
          return Promise.resolve({
            createdAt: null,
            eolsApp: app,
            featureName: key,
            featureValue: null,
            group: appStateRef.current.courseSectionId,
            updatedAt: null
          });
        }
        return postGroupFeatureFlagAction({
          eolsApp: app,
          featureName: key,
          group: appStateRef.current.courseSectionId,
          featureValue: newFlavor
        });
      }
      return Promise.reject(error);
    })
    .then((response) => {
      setAppState((prevState) => {
        return {
          ...prevState,
          pendingRequestCount: prevState.pendingRequestCount - 1,
          abTestFlavors: [
            ...prevState.abTestFlavors,
            response
          ]
        };
      });
      return response;
    })
    .catch((e) => handleRequestError({
      appState: appStateRef.current,
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchABFlavor',
      isTrackRequestCount: true

    }));
};

export const fetchAllAppFeatureFlagsAction = (props: AppContextState & {
  useCache: boolean;
}): Promise<FeatureFlagsGroupedDto[]> => {

  const {
    setAppState,
    appStateRef
  } = props;

  if (props.useCache && appStateRef.current.featureFlagsGrouped) {
    return Promise.resolve(appStateRef.current.featureFlagsGrouped);
  }
  requestStart({ setAppState });
  return fetchGroupedFeatureFlags().then((response) => {
    setAppState((prevState) => {
      return {
        ...prevState,
        featureFlagsGrouped: response,
        pendingRequestCount: prevState.pendingRequestCount - 1
      };
    });
    const promises = [];
    const activeABTests = getActiveABTestFlags(response, appStateRef.current.courseSectionId);

    if (activeABTests?.length) {
      activeABTests.forEach((activeABTest) => {
        promises.push(fetchABFlavorAction({
          abTestFlag: activeABTest,
          appState: appStateRef.current,
          appStateRef,
          setAppState
        }));
      });
    }

    if (promises.length) {
      return Promise.all(promises).then(() => {
        return response;
      });
    }

    return response;
  })
    .catch((e) => handleRequestError({
      appState: appStateRef.current,
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchAllAppFeatureFlagsAction',
      isTrackRequestCount: true

    }));
};

export const fetchCourseSectionAction = (props: AppContextState & {
  courseSectionId: string;
  useCache: boolean;
}): Promise<CourseSectionDto> => {
  const {
    appStateRef,
    setAppState,
    courseSectionId,
    useCache
  } = props;
  if (!courseSectionId) {
    return Promise.resolve(null);
  }
  if (useCache && appStateRef.current.currentCourse) {
    return Promise.resolve(appStateRef.current.currentCourse);
  }
  requestStart({ setAppState });
  return fetchCourseSectionService(courseSectionId)
    .then((response) => {
      setAppState((prevState) => {
        return {
          ...prevState,
          currentCourse: response,
          pendingRequestCount: prevState.pendingRequestCount - 1
        };
      });
      return response;
    })
    .catch((e) => handleRequestError({
      appState: appStateRef.current,
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchCourseSectionAction',
      isTrackRequestCount: true
    }));
};

export const appLinkReturnAction = (props: AppContextState & {
  linkId: string;
  returnPostBody: unknown;
}): Promise<void> => {
  const {
    linkId,
    returnPostBody,
  } = props;
  requestStart(props);
  return getLinkNavigation({ linkId, returnPostBody })
    .then(({ redirectUrl, linkId: responseLinkId }) => {
      setAppLinkCookies(responseLinkId);
      requestEnd(props);
      window.location.href = redirectUrl;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'returnAppLinkAction',
      isTrackRequestCount: true
    }));
};

export const fetchRaasAssessmentAction = (props: AppContextState & {
  id: number;
  studentStudyEolsAssessmentId: number;
  useCache: boolean;
}): Promise<RaasAssessmentDto> => {
  const {
    id,
    studentStudyEolsAssessmentId,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (useCache && appStateRef.current.remediationBaseState?.raasAssessment) {
    const cached = appStateRef.current.remediationBaseState?.raasAssessment;
    // Verify the cached assessment matches the requested one
    if (cached.studentStudyEolsAssessmentId === studentStudyEolsAssessmentId) {
      return Promise.resolve(cached);
    }
  }

  requestStart(props);
  return fetchRaasAssessment({ id, studentStudyEolsAssessmentId })
    .then((response: RaasAssessmentDto) => {
      updateRemediationBaseState(setAppState, {
        raasAssessment: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRecommendationGenerationStatusAction',
      isTrackRequestCount: true
    }));
};

export const fetchRecommendationsAction = (props: AppContextState & {
  assessmentId: number;
  recommendationIds: number[];
  useCache: boolean;
}): Promise<RecommendationDto[]> => {
  const {
    assessmentId,
    recommendationIds,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (useCache && appStateRef.current.remediationBaseState?.recommendations && appStateRef.current.remediationBaseState?.recommendations.length > 0) {
    const cachedRaasAssessment = appStateRef.current.remediationBaseState?.raasAssessment;
    if (cachedRaasAssessment && cachedRaasAssessment.studentStudyEolsAssessmentId === assessmentId) {
      return Promise.resolve(appStateRef.current.remediationBaseState?.recommendations);
    }
  }

  requestStart(props);
  return fetchRecommendations(assessmentId, recommendationIds)
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        recommendations: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRecommendationsAction',
      isTrackRequestCount: true
    }));
};

export const fetchRecommendationItemsAction = (props: AppContextState & {
  recommendationId: number;
  useCache: boolean;
}): Promise<RecommendationItemDto[]> => {
  const {
    recommendationId,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (useCache && appStateRef.current.remediationBaseState?.recommendationItems) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.recommendationItems);
  }

  requestStart(props);
  return fetchRecommendationItems(recommendationId)
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        recommendationItems: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRecommendationItemsAction',
      isTrackRequestCount: true
    }));
};

export const fetchRecommendationSourcesAction = (props: AppContextState & {
  recommendationIds?: number[];
  ids?: number[];
  useCache?: boolean;
}): Promise<RecommendationSourceDto[]> => {
  const {
    recommendationIds,
    ids,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (useCache && appStateRef.current.remediationBaseState?.recommendationSources) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.recommendationSources);
  }

  requestStart(props);
  return fetchRecommendationSources({ recommendationId: recommendationIds, id: ids })
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        recommendationSources: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRecommendationSourcesAction',
      isTrackRequestCount: true
    }));
};

export const fetchAssessmentByIdAction = (props: AppContextState & {
  assessmentId: number;
  userId: string;
  useCache: boolean;
}): Promise<EolsAssessmentDto> => {
  const {
    assessmentId,
    userId,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (useCache && appStateRef.current.remediationBaseState?.eolsAssessment && appStateRef.current.remediationBaseState?.eolsAssessment.id === assessmentId) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.eolsAssessment);
  }

  requestStart(props);
  return fetchEolsAssessmentById(userId, assessmentId)
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        eolsAssessment: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchAssessmentByIdAction',
      isTrackRequestCount: true
    }));
};

export const appLinkOutAction = (props: AppContextState & {
  config: AppLinkOutConfig;
  isNewTab?: boolean;
  token?: string;
}): Promise<void> => {
  const {
    appStateRef,
    config,
    isNewTab,
    token
  } = props;
  requestStart(props);
  return getExternalAppRedirectUrl(config, token)
    .then(({ redirectUrl, linkId }) => {

      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.APP_LINK_OUT,
          props: {
            linkType: config.action,
            srcApp: config.altSrcApp || Application.REMEDIATION,
            targetApp: config.app
          }
        }
      });

      setAppLinkCookies(linkId);

      if (isNewTab) {
        window.open(redirectUrl, '_blank');
      } else {
        window.location.href = redirectUrl;
      }
      requestEnd(props);
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'navigateToApp',
      isTrackRequestCount: true
    }));
};

export const getExternalAppRedirectUrlAction = (props: AppContextState & {
  config: AppLinkOutConfig;
  token?: string;
}): Promise<AppLinkOutResponse> => {
  const {
    config,
    token
  } = props;
  requestStart(props);
  return getExternalAppRedirectUrl(config, token)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'getExternalAppRedirectUrlAction',
      isTrackRequestCount: true
    }));
};

export const fetchRecommendationAttemptsAction = (props: AppContextState & {
  recommendationIds: number[];
  useCache: boolean;
}): Promise<RecommendationAttemptDto[]> => {
  const {
    recommendationIds,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (useCache && appStateRef.current.remediationBaseState?.recommendationAttempts) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.recommendationAttempts);
  }

  requestStart(props);
  return fetchRecommendationAttempts({ recommendationIds })
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        recommendationAttempts: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRecommendationAttemptsAction',
      isTrackRequestCount: true
    }));
};

export const fetchQuizSessionByRecommendationAttemptIdAction = (props: AppContextState & {
  recommendationAttemptId: number;
  useCache: boolean;
}): Promise<unknown> => {
  const {
    recommendationAttemptId,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (useCache && appStateRef.current.remediationBaseState?.quizAttemptDetails) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.quizAttemptDetails);
  }

  requestStart(props);
  return fetchQuizSessionByRecommendationAttemptId(recommendationAttemptId)
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        quizAttemptDetails: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchQuizSessionByRecommendationAttemptIdAction',
      isTrackRequestCount: true
    }));
};

export const postRecommendationAttemptAction = (props: AppContextState & {
  recommendationAttempt: Partial<RecommendationAttemptDto>;
}): Promise<RecommendationAttemptDto> => {
  const { recommendationAttempt } = props;
  requestStart(props);
  return postRecommendationAttempt(recommendationAttempt)
    .then((response) => {
      // Deliberately does not write the new attempt into remediationBaseState here.
      // The only consumers that used to rely on that write (EbookPlayer/OsmosisPlayer
      // reading the attempt back via getRecommendationAttemptById) now always refetch
      // attempts on mount instead (see withRemediationBase's useCache), so this action
      // stays a pure API call plus request-count tracking.
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postRecommendationAttemptAction',
      isTrackRequestCount: true
    }));
};

export const postRaaSQuizSessionAction = (props: AppContextState & {
  quizSessionDto: NewQuizSessionDto;
}): Promise<QuizSessionDto> => {
  const { quizSessionDto } = props;
  requestStart(props);
  return postRaaSQuizSession(quizSessionDto)
    // eslint-disable-next-line sonarjs/no-identical-functions
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postRaaSQuizSessionAction',
      isTrackRequestCount: true
    }));
};

export const fetchCaseStudyAction = (props: AppContextState & {
  caseStudyVtwId: string;
}): Promise<unknown> => {
  const { caseStudyVtwId } = props;
  requestStart(props);
  return fetchCaseStudy(caseStudyVtwId)
    // eslint-disable-next-line sonarjs/no-identical-functions
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchCaseStudyAction',
      isTrackRequestCount: true
    }));
};

export const fetchEolsAssessmentSubmissionsAction = (props: AppContextState & {
  assessmentIds: number[];
  useCache: boolean;
  stateKey: keyof Pick<RemediationBaseState, 'raasAssessmentSubmissions' | 'attemptAssessmentSubmissions'>;
}): Promise<AssessmentSubmissionDto[]> => {
  const {
    assessmentIds,
    useCache,
    appStateRef,
    setAppState,
    stateKey
  } = props;

  if (useCache && appStateRef.current.remediationBaseState?.[stateKey]) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.[stateKey]);
  }

  requestStart(props);
  return fetchEolsAssessmentSubmissions(assessmentIds)
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        [stateKey]: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchEolsAssessmentSubmissionsAction',
      isTrackRequestCount: true
    }));
};

export const fetchRemediationAssignmentWithSourcesAction = (props: AppContextState & {
  useCache: boolean;
  remediationAssignmentId: number;
}): Promise<unknown> => {
  const {
    useCache,
    appStateRef,
    setAppState,
    remediationAssignmentId
  } = props;

  // Cache check with ID comparison
  if (useCache && appStateRef.current.remediationBaseState?.remediationAssignmentWithSources && appStateRef.current.remediationBaseState?.remediationAssignmentWithSources.remediationAssignment.id === remediationAssignmentId) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.remediationAssignmentWithSources);
  }

  requestStart(props);
  return fetchRemediationAssignmentWithSources(remediationAssignmentId)
    // eslint-disable-next-line sonarjs/no-identical-functions
    .then((response) => {
      // Cache in base state
      updateRemediationBaseState(setAppState, {
        remediationAssignmentWithSources: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRemediationAssignmentWithSourcesAction',
      isTrackRequestCount: true
    }));
};

export const postAssessmentAction = (props: AppContextState & {
  userId: string;
  assessment: Partial<EolsAssessmentDto>;
}): Promise<EolsAssessmentDto> => {
  const {
    userId,
    assessment,
  } = props;
  requestStart(props);
  return postAssessment(userId, assessment)
    // eslint-disable-next-line sonarjs/no-identical-functions
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postAssessmentAction',
      isTrackRequestCount: true
    }));
};

export const putRecommendationAction = (props: AppContextState & {
  recommendation: RecommendationDto;
}): Promise<RecommendationDto> => {
  const { setAppState } = props;
  requestStart(props);
  return putRecommendation(props.recommendation)
    .then((response) => {
      setAppState((prev) => ({
        ...prev,
        remediationBaseState: {
          ...prev.remediationBaseState,
          recommendations: (prev.remediationBaseState?.recommendations ?? []).map((rec) => (rec.id === response.id ? response : rec)),
        },
      }));
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'putRecommendation',
      isTrackRequestCount: true
    }));
};

export const postSubmissionAction = (props: AppContextState & {
  submission: Partial<AssessmentSubmissionDto>;
}): Promise<AssessmentSubmissionDto> => {
  requestStart(props);
  return postSubmission(props.submission)
    // eslint-disable-next-line sonarjs/no-identical-functions
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postSubmissionAction',
      isTrackRequestCount: true
    }));
};

export const putRecommendationAttemptAction = (props: AppContextState & {
  recommendationAttempt: RecommendationAttemptDto;
}): Promise<RecommendationAttemptDto> => {
  requestStart(props);
  return putRecommendationAttempt(props.recommendationAttempt)
    // eslint-disable-next-line sonarjs/no-identical-functions
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'putRecommendationAttemptAction',
      isTrackRequestCount: true
    }));
};

export const fetchOsmosisTokenAction = (props: AppContextState): Promise<OsmosisTokenDto> => {
  const token = props.appStateRef.current.osmosisTokenDto;
  if (!shouldGetNewOsmosisToken(token)) {
    return Promise.resolve(token);
  }
  requestStart(props);
  return fetchOsmosisToken()
    .then((response) => {
      props.setAppState((prevState) => {
        return {
          ...prevState,
          osmosisTokenDto: response
        };
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchOsmosisTokenAction',
      isTrackRequestCount: true
    }));
};

export const fetchOsmosisTranscriptAction = (props: AppContextState & {
  learnName: string;
  auth: string;
}): Promise<{ transcriptData: unknown; transcript: string }> => {
  requestStart(props);
  return fetchOsmosisTranscript(props.learnName, props.auth)
    .then((response) => {
      const scriptLink = get(response, 'data.learnData.scriptLink');
      if (!scriptLink) {
        return Promise.reject(new Error('Missing scriptLink'));
      }
      return axios.get(scriptLink).then((_response: AxiosResponse<string>) => {
        requestEnd(props);
        return {
          transcriptData: response.data,
          transcript: _response.data
        };
      });
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchOsmosisTranscriptAction',
      isTrackRequestCount: true
    }));
};

export const fetchOsmosisLearnDataAction = (props: AppContextState & {
  learnNodeId: string;
}): Promise<OsmosisLearnData> => {
  requestStart(props);
  return fetchOsmosisLearnData(props.learnNodeId)
    .then((response) => {
      requestEnd(props);
      return response.data;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchOsmosisLearnDataAction',
      isTrackRequestCount: true
    }));
};

const EBOOK_CFI_NO_CONTENT_ERROR = 'EBOOK_CFI_NO_CONTENT';

export const fetchEbookSectionSsoUrlAction = (props: AppContextState & {
  sectionRequest: EbookSectionSsoRequestDto;
  recommendationId: number;
  recommendationAttemptId: number;
}): Promise<EbookReadingResponseDto> => {
  const trackingProps = {
    isbn: props.sectionRequest.isbn,
    cfi: props.sectionRequest.cfi,
    raasAssessmentId: props.appState.remediationBaseState.raasAssessment?.id ?? null,
    assessmentId: props.appState.remediationBaseState.eolsAssessment?.id ?? null,
    recommendationId: props.recommendationId,
    recommendationAttemptId: props.recommendationAttemptId
  };
  requestStart(props);
  return fetchEbookSectionSsoUrl(props.sectionRequest)
    .then((response) => {
      // The service returns 204 (empty body) when VitalSource's content check finds no
      // content for this CFI - treat that the same as a request failure so callers fall
      // back to the page-range flow, but track it separately from a genuine request
      // failure since it's an expected outcome, not an error.
      if (!response?.signInUrl) {
        trackNewRelicAction({
          appStateRef: props.appStateRef,
          actionProps: {
            action: NewRelicAnalyticsAction.EBOOK_CFI_LINK_RESULT,
            props: {
              status: 'no_content',
              ...trackingProps
            }
          }
        });
        requestEnd(props);
        throw new Error(EBOOK_CFI_NO_CONTENT_ERROR);
      }
      trackNewRelicAction({
        appStateRef: props.appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.EBOOK_CFI_LINK_RESULT,
          props: {
            status: 'success',
            ...trackingProps
          }
        }
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => {
      if (e.message === EBOOK_CFI_NO_CONTENT_ERROR) {
        return Promise.reject(e);
      }
      return handleRequestError({
        ...props,
        error: e,
        action: 'fetchEbookSectionSsoUrlAction',
        isTrackRequestCount: true
      });
    });
};

export const fetchEbookPageRangeSsoUrlAction = (props: AppContextState & {
  pageRangeRequest: EbookReadingRequestDto;
}): Promise<EbookReadingResponseDto> => {
  requestStart(props);
  return fetchEbookReadingSsoUrl(props.pageRangeRequest)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchEbookPageRangeSsoUrlAction',
      isTrackRequestCount: true
    }));
};

export const submitReviewAttemptAction = (props: AppContextState & {
  recommendationAttemptId: number;
  assessmentSubmission: Partial<AssessmentSubmissionDto>;
}): Promise<RecommendationAttemptDto> => {
  const { setAppState } = props;
  requestStart(props);
  return submitReviewAttempt(
    props.recommendationAttemptId,
    props.assessmentSubmission
  )
    .then((response) => {
      setAppState((prev) => ({
        ...prev,
        remediationBaseState: {
          ...prev.remediationBaseState,
          recommendationAttempts: (prev.remediationBaseState?.recommendationAttempts ?? []).map((attempt) => (attempt.id === response.id ? response : attempt)),
        },
      }));
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'submitReviewAttemptAction',
      isTrackRequestCount: true
    }));
};

export const fetchIpAddressAction = (props: AppContextState): Promise<string> => {
  if (props.appStateRef.current.ipAddress) {
    return Promise.resolve(props.appStateRef.current.ipAddress);
  }
  requestStart(props);
  return fetchIpAddress()
    .then((response) => {
      props.setAppState((prevState) => {
        return {
          ...prevState,
          ipAddress: response.data.ip
        };
      });
      requestEnd(props);
      return response.data.ip;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchIpAddressAction',
      isTrackRequestCount: true
    }));
};

export const fetchAssessmentsByRemediationAssignmentIdAction = (props: AppContextState & {
  remediationAssignmentId: number;
}): Promise<RaasAssessmentDto[]> => {
  const { remediationAssignmentId } = props;
  if (!remediationAssignmentId) {
    return Promise.resolve([]);
  }
  requestStart(props);
  return fetchAssessmentsByRemediationAssignmentId(remediationAssignmentId)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchAssessmentsByRemediationAssignmentIdAction',
      isTrackRequestCount: true
    }));
};

export const fetchMyRaasAssessmentsAction = (props: AppContextState): Promise<RaasAssessmentWithAssignmentDto[]> => {
  requestStart(props);
  return fetchMyRaasAssessments()
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchMyRaasAssessmentsAction',
      isTrackRequestCount: true
    }));
};

export const fetchUsersByIdsAction = (props: AppContextState & {
  ids: number[];
}): Promise<UsersByIdDto> => {
  const { ids } = props;
  if (!ids || !ids.length) {
    return Promise.resolve([]);
  }
  requestStart(props);
  return fetchUsersByIds(ids)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchUserListByIdsAction',
      isTrackRequestCount: true
    }));
};

export const fetchHesiExamInfoByExamEditionIdAction = (props: AppContextState & {
  editionId: number;
  useCache: boolean;
}): Promise<HesiExamInfoDto> => {
  const {
    editionId,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (!editionId) {
    return Promise.resolve(null);
  }

  if (useCache && appStateRef.current.remediationBaseState?.hesiExamInfo && appStateRef.current.remediationBaseState?.hesiExamInfo.editionId === editionId) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.hesiExamInfo);
  }

  requestStart(props);

  return fetchHesiExamInfoByExamEditionId(editionId)
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        hesiExamInfo: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => {
      handleRequestError({
        ...props,
        error: e,
        action: 'fetchHesiExamInfoByExamEditionIdAction',
        isTrackRequestCount: true
      });
      return null;
    });
};

export const patchRaasAssessmentLastAccessedAction = (props: AppContextState & {
  raasAssessmentId: number;
}): Promise<void> => {
  const { raasAssessmentId } = props;
  if (!raasAssessmentId) {
    return Promise.resolve();
  }
  requestStart(props);
  return patchRaasAssessmentLastAccessed(raasAssessmentId)
    .then(() => {
      requestEnd(props);
    })
    .catch((e) => {
      handleRequestError({
        ...props,
        error: e,
        action: 'patchRaasAssessmentLastAccessedAction',
        isTrackRequestCount: true
      });
      return undefined;
    })
};

export const fetchRaasAssessmentSourceAction = (props: AppContextState & {
  raasAssessmentId: number;
  useCache: boolean;
}): Promise<RaasAssessmentSourceDto[]> => {
  const {
    raasAssessmentId,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (!raasAssessmentId) {
    return Promise.resolve(null);
  }

  if (useCache && appStateRef.current.remediationBaseState?.raasAssessmentSourceList && appStateRef.current.remediationBaseState?.raasAssessment) {
    // Verify the cached sources belong to the requested assessment
    if (appStateRef.current.remediationBaseState?.raasAssessment.id === raasAssessmentId) {
      return Promise.resolve(appStateRef.current.remediationBaseState?.raasAssessmentSourceList);
    }
  }

  requestStart(props);
  return fetchRaasAssessmentSource(raasAssessmentId)
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        raasAssessmentSourceList: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRaasAssessmentSourceAction',
      isTrackRequestCount: true
    }));
}

export const fetchAssessmentMetricsAction = (props: AppContextState & {
  assessmentId: number;
  useCache: boolean;
}): Promise<RaasAssessmentMetricsDto> => {
  const {
    assessmentId,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (!assessmentId) {
    return Promise.resolve(null);
  }

  if (useCache && appStateRef.current.remediationBaseState?.assessmentMetrics && appStateRef.current.remediationBaseState?.assessmentMetrics.studentStudyEolsAssessmentId === assessmentId) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.assessmentMetrics);
  }

  requestStart(props);
  return fetchAssessmentMetrics(assessmentId)
    .then((response) => {
      updateRemediationBaseState(setAppState, {
        assessmentMetrics: response
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchAssessmentMetricsAction',
      isTrackRequestCount: true
    }));
}

export const fetchSherpathAIContextAction = (props: AppContextState & {
  plpAssignmentId: string;
  useCache: boolean;
}): Promise<SherpathAIContextDto> => {
  const {
    plpAssignmentId,
    useCache,
    appStateRef,
    setAppState
  } = props;

  if (!plpAssignmentId) {
    return Promise.resolve(null);
  }

  // Cache check: If useCache flag is true and context exists in base state
  if (useCache && appStateRef.current.remediationBaseState?.sherpathAIContext) {
    return Promise.resolve(appStateRef.current.remediationBaseState?.sherpathAIContext);
  }

  requestStart(props);
  return fetchSherpathAIContext(plpAssignmentId)
    .then((context) => {
      updateRemediationBaseState(setAppState, {
        sherpathAIContext: context
      });
      requestEnd(props);
      return context;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchSherpathAIContextAction',
      isTrackRequestCount: true
    }));
}

export const fetchRecommendationFeedbackAction = (props: AppContextState & {
  recommendationId: number;
}): Promise<RecommendationFeedbackDto> => {
  const { recommendationId } = props;
  requestStart(props);
  return fetchRecommendationFeedback(recommendationId)
    .then((response: RecommendationFeedbackDto) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRecommendationFeedbackAction',
      isTrackRequestCount: true
    }));
};

export const submitRecommendationFeedbackAction = (props: AppContextState & {
  dto: RecommendationFeedbackDto;
}): Promise<void> => {
  const { dto } = props;
  requestStart(props);
  return postRecommendationFeedback(dto)
    .then(() => {
      requestEnd(props);
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'submitRecommendationFeedbackAction',
      isTrackRequestCount: true
    }));
};

export const updateRecommendationFeedbackAction = (props: AppContextState & {
  dto: RecommendationFeedbackDto;
}): Promise<void> => {
  const { dto } = props;
  requestStart(props);
  return patchRecommendationFeedback(dto)
    .then(() => {
      requestEnd(props);
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'updateRecommendationFeedbackAction',
      isTrackRequestCount: true
    }));
};

/**
 * Helper function to update remediationBaseState in app state.
 * Use this instead of local setState in HOCs to maintain single source of truth.
 */
export const updateRemediationBaseState = (
  setAppState: (updateFn: (prevState: AppState) => AppState) => void,
  updates: Partial<AppState['remediationBaseState']>
): void => {
  setAppState((prevState) => ({
    ...prevState,
    remediationBaseState: {
      ...prevState.remediationBaseState,
      ...updates
    }
  }));
};
